import React, { useState, useRef, useEffect } from 'react';

import axios from '../../../axios';
import moment from 'moment';
import { BiChevronDown, BiChevronUp } from 'react-icons/bi';

import MultirangeDatePickerDesigned from '../Components/MultirangeDatePickerDesigned';
import TriviaInsightsV2 from '../Components/TriviaInsightsV2';

import * as url from '../../../utils/url';
import { toastContainer } from '../../../utils/toast';
import {
    buildHashtagInsightsDisabledReason,
    HASHTAG_INSIGHTS_ZERO_IN_RANGE,
    HASHTAG_INSIGHTS_GENERATION_FAILED,
    HASHTAG_INSIGHTS_CHECKING_ELIGIBILITY,
} from '../../../utils/triviaResultsStrings';

// `embedded` (HashtagDrilldown's solo-card use) skips the redundant header
// and opens by default — every other prop/state below is unaffected.
const HashtagInsightsCard = ({ groupId, hashtagLabel, description, createdAt, embedded }) => {
    const [isOpen, setIsOpen] = useState(!!embedded);
    const [hasLoaded, setHasLoaded] = useState(false);
    const [isLoadingDetail, setIsLoadingDetail] = useState(false);
    const [isGenerating, setIsGenerating] = useState(false);
    const [insight, setInsight] = useState(null);
    const [dateRange, setDateRange] = useState([]);
    const [maxRangeMonths, setMaxRangeMonths] = useState(3);
    const [minEligibleMessages, setMinEligibleMessages] = useState(5);
    const [eligibleMessageCount, setEligibleMessageCount] = useState(0);
    const [isRangeEligible, setIsRangeEligible] = useState(false);
    const [isRefreshingEligibility, setIsRefreshingEligibility] = useState(false);
    const [isDetailExpanded, setIsDetailExpanded] = useState(true);
    // Guards against an older refreshEligibility() call resolving after a
    // newer one and clobbering its (more current) result.
    const eligibilityRequestRef = useRef(0);

    const groupCreatedAtDate = createdAt ? new Date(createdAt) : null;
    const nowDate = new Date();

    const refreshEligibility = async (start, end) => {
        const requestId = ++eligibilityRequestRef.current;
        setIsRefreshingEligibility(true);
        try {
            const res = await axios.get(url.textifyGetGroupInsights(groupId), {
                params: { analysisStartAt: start.toISOString(), analysisEndAt: end.toISOString() },
            });
            if (requestId !== eligibilityRequestRef.current) return;
            const data = res.data || {};
            setEligibleMessageCount(data.eligibleMessageCount || 0);
            setIsRangeEligible(!!data.canGenerate);
        } catch (err) {
            if (requestId !== eligibilityRequestRef.current) return;
            console.error('[HashtagInsightsCard] refresh eligibility error:', err);
            toastContainer('Failed to check message eligibility for this range.', 'error');
        } finally {
            if (requestId === eligibilityRequestRef.current) setIsRefreshingEligibility(false);
        }
    };

    const loadDetail = async () => {
        setIsLoadingDetail(true);
        try {
            const res = await axios.get(url.textifyGetGroupInsights(groupId));
            const data = res.data || {};
            setMaxRangeMonths(data.maxRangeMonths || 3);
            setMinEligibleMessages(data.minEligibleMessages || 5);

            const storedInsight = data.insight;
            if (storedInsight && storedInsight.analysisStartAt && storedInsight.analysisEndAt) {
                setInsight(storedInsight);
                setIsGenerating(storedInsight.status === 'PROCESSING');
                const storedStart = new Date(storedInsight.analysisStartAt);
                const storedEnd = new Date(storedInsight.analysisEndAt);
                setDateRange([storedStart, storedEnd]);
                // Staleness basis: live eligible count for the SAME range the
                // stored insight was generated for (not the widest default).
                await refreshEligibility(storedStart, storedEnd);
            } else {
                setInsight(null);
                setIsGenerating(false);
                setDateRange([new Date(data.analysisStartAt), new Date(data.analysisEndAt)]);
                setEligibleMessageCount(data.eligibleMessageCount || 0);
                setIsRangeEligible(!!data.canGenerate);
            }
        } catch (err) {
            console.error('[HashtagInsightsCard] load detail error:', err);
            toastContainer('Failed to load this hashtag\'s insights.', 'error');
        } finally {
            setIsLoadingDetail(false);
            setHasLoaded(true);
        }
    };

    const toggleOpen = () => {
        const nextOpen = !isOpen;
        setIsOpen(nextOpen);
        if (nextOpen) {
            loadDetail();
        }
    };

    // Embedded mode has no header click to trigger this — load once on mount.
    useEffect(() => {
        if (embedded) {
            loadDetail();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // react-multi-date-picker's onChange returns its own DateObject
    // instances, which moment() can't parse (it silently defaults to "now").
    const toJsDate = (value) => {
        if (value && typeof value.toDate === 'function') return value.toDate();
        return moment(value).toDate();
    };

    const handleDateChange = (newRange) => {
        if (!newRange || newRange.length < 2) {
            setDateRange(newRange || []);
            return;
        }

        let start = toJsDate(newRange[0]);
        let end = toJsDate(newRange[1]);
        let clamped = false;

        if (groupCreatedAtDate && start.getTime() < groupCreatedAtDate.getTime()) {
            start = groupCreatedAtDate;
            clamped = true;
        }
        if (end.getTime() > nowDate.getTime()) {
            end = nowDate;
            clamped = true;
        }

        const maxEnd = moment(start).add(maxRangeMonths, 'months').toDate();
        if (end.getTime() > maxEnd.getTime()) {
            end = maxEnd;
            clamped = true;
        }

        if (clamped) {
            toastContainer(`Date range clamped to ${maxRangeMonths} months.`, 'info');
        }

        setDateRange([start, end]);
        refreshEligibility(start, end);
    };

    const runGenerate = async () => {
        if (!dateRange || dateRange.length < 2) return;

        setIsGenerating(true);
        try {
            const res = await axios.post(url.textifyGenerateGroupInsights(groupId), {
                analysisStartAt: dateRange[0],
                analysisEndAt: dateRange[1],
            });
            const data = res.data || {};
            setInsight(data.insights || null);
            if (data.insights && typeof data.insights.sourceMessageCount === 'number') {
                setEligibleMessageCount(data.insights.sourceMessageCount);
            }
        } catch (err) {
            console.error('[HashtagInsightsCard] generate error:', err);
            const responseData = err && err.response && err.response.data;
            const serverMessage = responseData && (responseData.message || responseData.error);
            toastContainer(serverMessage || HASHTAG_INSIGHTS_GENERATION_FAILED, 'error');
        } finally {
            setIsGenerating(false);
        }
    };

    const hasCompletedInsight = !!(insight && insight.status === 'COMPLETED');
    const disabledReason = isRefreshingEligibility
        ? HASHTAG_INSIGHTS_CHECKING_ELIGIBILITY
        : !isRangeEligible
            ? buildHashtagInsightsDisabledReason(eligibleMessageCount, minEligibleMessages)
            : null;

    const isViewingStoredRange = hasCompletedInsight && dateRange[0] && dateRange[1] &&
        new Date(dateRange[0]).getTime() === new Date(insight.analysisStartAt).getTime() &&
        new Date(dateRange[1]).getTime() === new Date(insight.analysisEndAt).getTime();
    const staleDelta = (isViewingStoredRange && typeof insight.sourceMessageCount === 'number' && eligibleMessageCount > insight.sourceMessageCount)
        ? eligibleMessageCount - insight.sourceMessageCount
        : 0;

    const startedCaption = groupCreatedAtDate
        ? `#${hashtagLabel} started ${moment(groupCreatedAtDate).format('MMM D, YYYY')}`
        : '';

    return (
        <div className="uc-insights-card">
            {!embedded && (
                <div className="uc-insights-card-header" onClick={toggleOpen}>
                    <div className="uc-header-area">
                        <h2>#{hashtagLabel}</h2>
                        {description && <p>{description}</p>}
                    </div>
                    <span className="uc-insights-expand-toggle">
                        {isOpen ? <BiChevronUp size={20} /> : <BiChevronDown size={20} />}
                    </span>
                </div>
            )}

            {isOpen && (
                <div className={'uc-insights-card-body' + (embedded ? ' uc-insights-card-body-embedded' : '')}>
                    {isLoadingDetail && !hasLoaded ? (
                        <div className="uc-insights-card-loading">
                            <p className="text-muted mb-0">Loading...</p>
                        </div>
                    ) : (
                        <React.Fragment>
                            <div className="uc-insights-date-wrap">
                                <MultirangeDatePickerDesigned
                                    noAlert
                                    dateRange={dateRange}
                                    handleDateChange={handleDateChange}
                                    minDate={groupCreatedAtDate}
                                    maxDate={nowDate}
                                    calendarClassName="uc-gray-datepicker"
                                />
                                <span className="uc-insights-range-caption">
                                    Up to {maxRangeMonths} months at a time{startedCaption ? ` · ${startedCaption}` : ''}
                                </span>
                            </div>

                            {eligibleMessageCount === 0 && (
                                <p className="uc-insights-zero-range">{HASHTAG_INSIGHTS_ZERO_IN_RANGE}</p>
                            )}

                            {staleDelta > 0 && (
                                <p className="uc-insights-staleness-note">
                                    {staleDelta} new message{staleDelta === 1 ? '' : 's'} since this was generated.
                                </p>
                            )}

                            {hasCompletedInsight && (
                                <div className="uc-insights-meta">
                                    <p>
                                        Based on {insight.sourceMessageCount} employee message{insight.sourceMessageCount === 1 ? '' : 's'}
                                    </p>
                                    <p>
                                        {moment(insight.analysisStartAt).format('MMMM D, YYYY')} – {moment(insight.analysisEndAt).format('MMMM D, YYYY')}
                                    </p>
                                    <p>
                                        Generated {moment(insight.lastGeneratedAt).format('MMMM D, YYYY [at] h:mm A')}
                                    </p>
                                </div>
                            )}

                            <TriviaInsightsV2
                                messaging
                                hideHeader
                                insights={hasCompletedInsight ? insight : null}
                                isExpanded={isDetailExpanded}
                                isLoading={isGenerating}
                                enhancedLoading
                                canRegenerate={hasCompletedInsight}
                                canGenerate={!hasCompletedInsight}
                                generateDisabled={!isRangeEligible || isRefreshingEligibility}
                                regenerateDisabled={!isRangeEligible || isRefreshingEligibility}
                                onGetFullInsights={() => setIsDetailExpanded(!isDetailExpanded)}
                                onRegenerate={runGenerate}
                                onGenerate={runGenerate}
                            />

                            {disabledReason && (
                                <p className="uc-insights-disabled-reason">{disabledReason}</p>
                            )}
                        </React.Fragment>
                    )}
                </div>
            )}
        </div>
    );
};

export default HashtagInsightsCard;
